import { useEffect, useRef } from 'react';
import { initGTM, trackPageView } from '../integrations/gtm';
import useAnalytics from '@/hooks/useAnalytics';

interface GTMProviderProps {
  children: React.ReactNode;
}

const GTMProvider = ({ children }: GTMProviderProps) => {
  const { trackPageView: trackAnalyticsPageView } = useAnalytics();
  const isInitializedRef = useRef(false);
  
  // Listen for meta tags being set
  useEffect(() => {
    const handleMetaTagsSet = (event: Event) => {
      const customEvent = event as CustomEvent<{ 
        title: string, 
        description: string,
        url: string,
        image?: string
      }>;
      
      if (customEvent.detail && isInitializedRef.current) {
        const { title, url } = customEvent.detail;
        
        // Only track analytics without modifying meta tags
        console.log('Meta tags set, tracking page view:', title, url);
        trackPageView(url, title);
        trackAnalyticsPageView(url, title);
      }
    };
    
    // Add event listener for meta tags being set
    document.addEventListener('meta_tags_set', handleMetaTagsSet);
    
    // Cleanup
    return () => {
      document.removeEventListener('meta_tags_set', handleMetaTagsSet);
    };
  }, [trackAnalyticsPageView]);
  
  // Initialize GTM with a delay
  useEffect(() => {
    // Delay GTM initialization to give MetaTags components time to set up properly
    const timer = setTimeout(() => {
      // Check if meta tags are currently being set
      const isSettingMetaTags = window.document.documentElement.hasAttribute('data-setting-meta-tags');
      
      if (isSettingMetaTags) {
        // If meta tags are being set, wait a bit longer
        console.log('Meta tags are being set, delaying GTM initialization');
        setTimeout(() => {
          initGTM();
          isInitializedRef.current = true;
          console.log('GTM initialized from GTMProvider (delayed)');
        }, 500);
      } else {
        // Initialize GTM without modifying meta tags
        initGTM();
        isInitializedRef.current = true;
        console.log('GTM initialized from GTMProvider');
      }
      
      // Track initial page view with current URL but don't modify meta tags
      const initialPath = window.location.pathname;
      const initialTitle = document.title;
      
      // Preserve existing meta tags by only sending analytics events
      setTimeout(() => {
        trackPageView(initialPath, initialTitle);
        trackAnalyticsPageView(initialPath, initialTitle);
      }, 200); // Small delay to ensure GTM is ready
    }, 1000); // Increased delay to ensure MetaTags are set first
    
    // Listen for page title updates to track page views
    // But don't modify meta tags here
    const handleTitleChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ title: string, path?: string }>;
      if (customEvent.detail && isInitializedRef.current) {
        const { title, path } = customEvent.detail;
        const currentPath = path || window.location.pathname;
        
        // Track analytics without modifying meta tags
        trackPageView(currentPath, title);
        trackAnalyticsPageView(currentPath, title);
      }
    };
    
    // Add event listener for title changes
    document.addEventListener('page_title_updated', handleTitleChange);
    
    // Cleanup
    return () => {
      clearTimeout(timer);
      document.removeEventListener('page_title_updated', handleTitleChange);
    };
  }, [trackAnalyticsPageView]);

  return <>{children}</>;
};

export default GTMProvider;
